import { Link } from 'react-router-dom';
import { usePageData } from '@/hooks/usePageData';
import { dashboardQuery } from '@/lib/pageQueries';
import { formatJobDates } from '@/lib/dates';
import { StatusPill, quoteStatusTone } from '@/components/ui/StatusPill';
import { SkeletonSections } from '@/components/ui/Skeleton';
const card: React.CSSProperties = {
  background: '#fff',
  borderRadius: 10,
  padding: 20,
  boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
  marginBottom: 24,
};

export function DashboardPage() {
  const { data, error } = usePageData(dashboardQuery.key, dashboardQuery.fetch);

  if (error && !data) return <p style={{ color: '#c33' }}>Failed to load dashboard</p>;
  if (!data) return <SkeletonSections sections={4} />;

  return (
    <div style={{ maxWidth: 1000 }}>
      {/* Overdue returns */}
      {data.overdueJobs && data.overdueJobs.length > 0 && (
        <div style={{ ...card, borderLeft: '4px solid #c33' }}>
          <h2 style={{ fontSize: 15, fontWeight: 700, marginBottom: 14, color: '#c33' }}>
            Overdue Returns
          </h2>
          {data.overdueJobs.map(({ job, unreturnedCount }) => (
            <div key={job.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f0f0f0', fontSize: 13 }}>
              <div>
                <Link to={`/warehouse/${job.id}`} style={{ fontWeight: 600, color: '#c33', textDecoration: 'none' }}>
                  {job.jobNumber ? `J-${job.jobNumber}: ` : ''}{job.name}
                </Link>
                <span style={{ color: '#888', marginLeft: 8 }}>{job.client?.name}</span>
              </div>
              <span style={{ color: '#c33', fontWeight: 600 }}>
                {unreturnedCount} unit{unreturnedCount !== 1 ? 's' : ''} unreturned
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Currently out */}
      <div style={{ ...card, borderLeft: '4px solid #6c63ff' }}>
        <h2 style={{ fontSize: 15, fontWeight: 700, marginBottom: 14, color: 'var(--brand)' }}>
          Currently Out
        </h2>
        {data.currentJobs.length === 0 ? (
          <p style={{ color: '#888', fontSize: 13 }}>Nothing currently out.</p>
        ) : (
          data.currentJobs.map((job) => (
            <div key={job.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f0f0f0', fontSize: 13 }}>
              <div>
                <Link to={`/jobs/${job.id}`} style={{ fontWeight: 600, color: 'var(--brand)', textDecoration: 'none' }}>
                  J-{job.jobNumber}: {job.name}
                </Link>
                <span style={{ color: '#888', marginLeft: 8 }}>{job.client?.name}</span>
              </div>
              <span style={{ color: '#555' }}>
                {formatJobDates(job.eventStartDate, job.eventEndDate)}
              </span>
            </div>
          ))
        )}
      </div>

      {/* Upcoming jobs */}
      <div style={card}>
        <h2 style={{ fontSize: 15, fontWeight: 700, marginBottom: 14 }}>
          Upcoming Jobs (next 30 days)
        </h2>
        {data.upcomingJobs.length === 0 ? (
          <p style={{ color: '#888', fontSize: 13 }}>No upcoming jobs.</p>
        ) : (
          data.upcomingJobs.map((job) => (
            <div key={job.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f0f0f0', fontSize: 13 }}>
              <div>
                <Link to={`/jobs/${job.id}`} style={{ fontWeight: 600, color: 'var(--brand)', textDecoration: 'none' }}>
                  J-{job.jobNumber}: {job.name}
                </Link>
                <span style={{ color: '#888', marginLeft: 8 }}>{job.client?.name}</span>
              </div>
              <span style={{ color: '#555' }}>
                {formatJobDates(job.eventStartDate, job.eventEndDate)}
              </span>
            </div>
          ))
        )}
      </div>

      {/* Pending quotes */}
      <div style={card}>
        <h2 style={{ fontSize: 15, fontWeight: 700, marginBottom: 14 }}>
          Quotes Awaiting Action
        </h2>
        {data.pendingQuotes.length === 0 ? (
          <p style={{ color: '#888', fontSize: 13 }}>All caught up.</p>
        ) : (
          data.pendingQuotes.map((quote) => (
            <div key={quote.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f0f0f0', fontSize: 13 }}>
              <div>
                <Link to={`/jobs/${quote.job?.id}`} style={{ fontWeight: 600, color: 'var(--brand)', textDecoration: 'none' }}>
                  {quote.job?.jobNumber ? `J-${quote.job.jobNumber}: ` : ''}{quote.job?.name}
                </Link>
                <span style={{ color: '#888', marginLeft: 8 }}>{quote.job?.client?.name}</span>
              </div>
              <StatusPill label={quote.status} tone={quoteStatusTone(quote.status)} />
            </div>
          ))
        )}
      </div>

      {/* Low availability */}
      <div style={card}>
        <h2 style={{ fontSize: 15, fontWeight: 700, marginBottom: 14 }}>
          Zero Availability (next 30 days)
        </h2>
        {data.lowAvailability.length === 0 ? (
          <p style={{ color: '#888', fontSize: 13 }}>All inventory available.</p>
        ) : (
          data.lowAvailability.map(({ item }) => (
            <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f0f0f0', fontSize: 13 }}>
              <Link to={`/inventory/${item.id}`} style={{ fontWeight: 600, color: 'var(--brand)', textDecoration: 'none' }}>
                {item.name}
              </Link>
              <span style={{ color: '#c33', fontWeight: 600 }}>Fully booked</span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
